export const SITE_NAME = "Rate check";
export const SITE_TITLE = "Home loan rate check Australia | Am I overpaying?";
export const SITE_DESCRIPTION =
  "Australian home loan rate check from Azure Home Loans. Compare the rate on your statement with eligible new-customer prints, see a yearly gap, then ask your bank to review or speak with a broker. No credit check. General information only.";

export const PAGE_H1 = "Home loan rate check Australia";
export const PAGE_H1_EM = "Am I overpaying?";

/** 40–70 words. Citation-ready. Names the broker, the borrower, and the next step. */
export const INTRO_ANSWER =
  "Azure Home Loans is an Australian mortgage broker. This rate check is for owner-occupiers and investors who already have a loan and want to know if the rate on their statement sits above an eligible new-customer print. Enter the rate, balance and years left. You see a yearly gap with no credit check. Then ask your bank to review, or speak with a broker if a refinance may suit.";

export const TRUST_LINE =
  "Bishnu Adhikari — ACR 538895, authorised under ACL 390261 (Yellow Brick Road). General information only. Credit is subject to lender approval and a responsible-lending assessment.";

export const SEO_TOPICS = [
  "home loan rate check Australia",
  "how can I check my home loan interest rate",
  "am I overpaying on my home loan",
  "compare home loan rates Australia",
  "refinance calculator Australia",
  "home loan rate review",
  "refinance home loan",
  "home loan comparison",
  "comparison rate vs interest rate",
];

export const SEO_MODIFIED = "2026-08-17";
export const CITED_AS_AT = "19 Aug 2026";

/** Named prints already shown in the well. Do not invent extra lenders or rates. */
export const CITED_PRINTS = [
  {
    lender: "ING",
    product: "Mortgage Simplifier 70-80%",
    rate: 6.04,
    comparisonRate: 6.07,
    lvr: "owner-occupier P&I, LVR 70.01–80%, min $150,000",
    note: "CDR-matched. Do not quote the 5.99% ING band on an 80% file. Not Orange Advantage.",
  },
  {
    lender: "IMB",
    product: "Budget Home Loan 70-80%",
    rate: 6.04,
    comparisonRate: 6.07,
    lvr: "owner-occupier P&I, LVR 70.01–80%",
    note: "Held print. Do not quote the 5.99% IMB Budget band on an 80% file.",
  },
  {
    lender: "Macquarie Bank",
    product: "Basic Home Loan 70-80%",
    rate: 6.09,
    comparisonRate: 6.11,
    lvr: "owner-occupier P&I, LVR 70–80%",
    note: "Page and CDR. Do not quote the 6.04% ≤70% band on an 80% file. Not the Offset product.",
  },
] as const;

export const CHECK_NUMBERS = [
  "Interest rate on the statement",
  "Balance owing",
  "Years left — do not reset to 30",
  "Owner-occupier or investment",
  "Principal and interest or interest only",
  "LVR band, or loan ÷ your own valuation",
  "Offset balance, if any",
];

export type FaqItem = { q: string; a: string };

export const FAQ: FaqItem[] = [
  {
    q: "How can I check my home loan interest rate?",
    a: "Read the interest rate on your latest statement or in internet banking — not the comparison rate. Then enter that rate, the balance and the years left into the Azure Home Loans rate check. The tool compares it with eligible new-customer prints this brokerage can place at your occupancy, repayments and LVR, and shows a yearly gap. No credit check. Confirm any named print on the lender’s own page before you call the bank.",
  },
  {
    q: "How do I check if I am overpaying on my home loan?",
    a: "Enter the interest rate, loan amount and years left from your latest statement, plus occupancy, repayments and LVR. Azure Home Loans compares that rate with eligible owner-occupier or investment prints we can place and shows a yearly gap. ASIC and the ACCC have reported that some existing borrowers pay more than new customers on comparable loans. Whether a gap is worth a rate review or a refinance depends on fees, cashback and whether your bank will match.",
  },
  {
    q: "Is this a refinance calculator for Australia?",
    a: "It is a rate check first, then a refinance calculator. You see named products and comparison rates, then an illustrative cost to switch after cashback and a break-even in months. Keep your remaining term in the model. Resetting to 30 years can look cheaper per month while costing more interest over the life of the loan.",
  },
  {
    q: "Should I ask my bank to match, or refinance?",
    a: "A rate review with your current lender often does not need a full application or a credit enquiry. Take a named product to retention if the numbers support it. If they will not get close, a refinance may suit — after you count discharge and application fees, minus any cashback. That stay-or-switch order is how this tool is built. It is not a recommendation to switch.",
  },
  {
    q: "Does a home loan rate check do a credit check?",
    a: "No. Typing your rate here does not touch your credit file. A credit enquiry usually starts only if you apply to refinance or a lender runs a full assessment. Stay and negotiate first if the numbers support it.",
  },
  {
    q: "What is the difference between a comparison rate and an interest rate?",
    a: "The interest rate is the rate used to calculate interest. A comparison rate folds in certain fees so a cheap headline rate with a fat package fee is harder to hide. By law the example is a $150,000 loan over 25 years. WARNING: that comparison rate is true only for the examples given and may not include all fees and charges. Different terms, fees or loan amounts can produce a different comparison rate.",
  },
  {
    q: "Do I have to give my email to see if I am overpaying?",
    a: "No. The rate check and the yearly gap are free, with no credit check. Email is only if you want the bank-call pack — the cited prints and the words to say to retention. That is optional, not a wall in front of the check.",
  },
  {
    q: "Can Azure Home Loans value my property for free?",
    a: "No reputable Australian AVM — CoreLogic, PropTrack, Domain — is free at property level. Enter your own estimate and we calculate LVR from loan amount ÷ that figure. Address autocomplete only fills the street. It does not invent a bank valuation.",
  },
  {
    q: "Is typing my rate credit assistance?",
    a: "No. Typing your rate is not a credit check and is not credit assistance. If you ask us to email the script or to switch the loan, we collect name, email and mobile only after you tick privacy consent. Credit assistance starts only when a licensed broker acts. Read the Privacy Policy and Credit Guide on azurehomeloans.com.au before you send anything.",
  },
];

export const HOW_TO_STEPS = [
  {
    name: "Enter the loan you have",
    text: "Use the interest rate, loan amount and years left from your statement. If you know LVR, pick the band. If not, enter your own valuation and we calculate it. Address is optional.",
  },
  {
    name: "Read the gap",
    text: "If your rate sits above an eligible print, the yearly figure is what that gap may cost if it stays. Confirm the print on the lender’s own page before you call.",
  },
  {
    name: "Stay: take a named product to the bank",
    text: "Copy the script. Name the lender and product. Push past the first cut. Ask for a discharge authority if they stall. A match is not guaranteed.",
  },
  {
    name: "Switch: refinance only if stay fails",
    text: "Check break-even after discharge and application fees, minus any cashback. If stay fails and a switch may suit, leave a name, email and mobile, tick privacy consent, and a broker can look at the file.",
  },
];

export const COMPARE_POINTS = [
  {
    name: "Rate review",
    text: "Same bank. Same loan. A named product and a script. Often no credit enquiry if they match. Suitability still depends on your file.",
  },
  {
    name: "Refinance",
    text: "New lender. Discharge fee, application, cashback, break-even. We only move a loan after you ask and after a responsible-lending assessment.",
  },
];

export const WHO_IT_SUITS = [
  {
    name: "Owner-occupiers and investors",
    text: "People who already have an Australian home loan and can read the rate, balance and remaining term from a statement.",
  },
  {
    name: "Self-employed borrowers",
    text: "The check still works from the rate you pay. A broker review is where PAYG vs self-employed servicing, BAS and add-backs get tested — this tool does not do that.",
  },
  {
    name: "Low-deposit or LMI files",
    text: "A higher LVR can close cheaper bands. Calculate LVR from your own valuation, or pick the band you know. For an LMI estimate see the calculator on the main site.",
  },
  {
    name: "First home buyers",
    text: "This page is for a loan you already have. If you are buying, use the first home buyer guide and grants on firsthomebuyers.gov.au — then speak with a broker.",
  },
];

export const HUB_LINKS = [
  { href: "/how-to-check-home-loan-rate.html", label: "How to check your rate" },
  { href: "https://azurehomeloans.com.au/rate-check", label: "Rate check on the main site" },
  { href: "https://azurehomeloans.com.au/refinance-playground", label: "Refinance hub" },
  { href: "https://azurehomeloans.com.au/calculators", label: "Calculators" },
  { href: "https://azurehomeloans.com.au/calculators/lmi-calculator", label: "LMI calculator" },
  { href: "https://azurehomeloans.com.au/first-home-buyer-guide", label: "First home buyer guide" },
  { href: "https://azurehomeloans.com.au/self-employed-home-loan", label: "Self-employed home loan" },
  { href: "https://azurehomeloans.com.au/mortgage-broker-sydney", label: "Mortgage broker Sydney" },
  { href: "https://azurehomeloans.com.au/contact", label: "Contact" },
  { href: "https://azurehomeloans.com.au/apply", label: "Apply" },
];
